// 像素美术调色板：canvas 逐像素绘制用色，与主题无关，保留
/* 漫剧小镇 · 像素办公室引擎（框架无关模块）
 * 移植自 pixel-office-prototype/game.js V1.1，零依赖 Canvas 2D，全部精灵/瓦片程序化绘制。
 * 渲染管线：场景绘制到 480×270 离屏缓冲，再按整数倍（×N × DPR）放大合成，保证逐像素锐利。
 *
 * createGame(canvas, hooks) -> {
 *   mount(sceneDescriptor),   // 挂载数据驱动场景（地图/名牌/地垫/出生点/家具）
 *   destroy(),                // 取消 RAF、移除全部 window 监听、断开 ResizeObserver、关闭 AudioContext
 *   setAvatar(resolvedAvatar),// resolvedAvatar: { name, skin:{main,dark}, hair:{main,light}, shirt:{main,dark} }
 *   setSound(on), isSoundOn(),
 *   setPaused(paused),        // 弹窗打开时暂停移动与交互（NPC 与环境动画继续）
 *   walkToTile(tx, ty),       // 沿可行走路径前往指定地垫，不直接触发地垫动作
 *   resetPosition(),          // 回到当前场景出生点，并清除该场景保存的位置
 * }
 *
 * sceneDescriptor = {
 *   grid: string[],           // 瓦片字符画：'.' 地板 '#' 墙 'T' 工位桌 'P' 盆栽 'S' 沙发 'M' 咖啡机
 *                             // 'B' 公告栏 'H' 荣誉墙 'R' 前台 'K' 电钢琴 'a'-'e' 地毯 '1'-'9'/'q' 门垫
 *   labels: [{ x, y, text, small }],   // 桌牌/区域牌（x,y 为瓦片坐标，文字水平居中对齐 x+0.5 格中心）
 *   mats: [{ tx, ty, label, action }], // 门垫：踏上后提示 label，Enter 触发 action（由 onAction 回调交给宿主）
 *   spawn: { x, y },          // 出生点（瓦片坐标，可为小数）
 *   crumb: string,            // 场景标题（通过 onCrumb 回调输出）
 *   id: string,               // 可选，稳定场景 ID；用于记住玩家上次位置
 *   npcs: [{ name, x, y, activity, lines, wanderRadius, stationary }],
 *                             // 可选，NPC 活动与对话（x,y 为瓦片坐标）
 *   hotspots: [{ tx, ty, range, label, action }], // 可选，靠近家具即可交互的热点
 *   artworks: [{ id, tx, ty, widthTiles, heightTiles, thumbnailUrl, isVideo, duration, name }],
 *                             // 可选，资源画廊画作；缩略图由引擎按视口懒加载
 *   coffee: { tx, ty } | null,// 可选，咖啡机位置：蒸汽粒子 + 靠近彩蛋
 *   ambience: 'local' | null, // 可选，按本地时间叠加轻微环境色
 * }
 *
 * hooks = { onPrompt, onCrumb, onToast, onAction, onEscape }
 */
'use strict';
import { drawReceptionCounter, drawStudioProp } from './pixelStudioProps.js';

// ============================ 基础 ============================
export const TILE = 32;            // 瓷砖边长（场景像素）
export const VIEW_W = 480;         // 视口（场景像素）
export const VIEW_H = 270;

export const C = {
  ink: '#264653', cream: '#f5f0e1', creamDark: '#e8dcc4', creamShadow: '#d8cbae',
  coral: '#f4a261', coralDark: '#e08a45', gold: '#e9c46a', goldDark: '#d4a744',
  mint: '#a8dadc', mintDark: '#8bc4c2', lavender: '#cdb4db', lavenderDark: '#b79fc9',
  peach: '#ffd6a5', peachDark: '#eeb98a',
  wall: '#2a4a56', wallDark: '#1b323b', wallLight: '#3a5f6c',
  wood: '#b08968', woodDark: '#7f5539', woodLight: '#c89f7d',
  skin: '#f2c894', skinDark: '#e0b07e', hair: '#4a3728', hairLight: '#6b4f3a',
  leaf: '#3d9e5a', leafDark: '#2d9e8a',
};

// ============================ 精灵 ============================
function makeSprite(rows, palette) {
  const w = rows[0].length, h = rows.length;
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const g = cv.getContext('2d');
  rows.forEach((row, y) => {
    [...row].forEach((ch, x) => {
      if (ch === '.' || ch === ' ') return;
      g.fillStyle = palette[ch] || '#000';
      g.fillRect(x, y, 1, 1);
    });
  });
  return cv;
}
function flipSprite(cv) {
  const out = document.createElement('canvas');
  out.width = cv.width; out.height = cv.height;
  const g = out.getContext('2d');
  g.translate(cv.width, 0); g.scale(-1, 1);
  g.drawImage(cv, 0, 0);
  return out;
}

// 小人：16x22。k=描边 h=发 H=发高光 s=肤 S=肤影 e=眼 b=衣 d=衣影 p=裤 w=鞋
const DEFAULT_CHAR_PALETTE = {
  k: C.ink, h: C.hair, H: C.hairLight, s: C.skin, S: C.skinDark,
  e: C.ink, b: C.coral, d: C.coralDark, p: C.ink, w: C.cream,
};
const NPC_PALETTES = [
  { ...DEFAULT_CHAR_PALETTE, b: C.mint, d: C.mintDark },
  { ...DEFAULT_CHAR_PALETTE, b: C.gold, d: C.goldDark },
  { ...DEFAULT_CHAR_PALETTE, b: '#8eabb6', d: '#708d9a', h: '#353c46', H: '#53606b' },
  { ...DEFAULT_CHAR_PALETTE, b: C.lavender, d: C.lavenderDark, h: '#735747', H: '#97765b' },
  { ...DEFAULT_CHAR_PALETTE, b: '#a7b89c', d: '#82967b', h: '#574e43', H: '#7d705f' },
];
const DOWN_0 = [
  '................',
  '.....kkkkkk.....',
  '....khhhhhhk....',
  '...khhhhhhhhk...',
  '..khhHHhhhhhhk..',
  '..khHHhhhhhhh k..'.replace(' ', ''),
  '..khsssssssshk..',
  '..kssssssssssk..',
  '..ksekssskessk..',
  '..kssssssssssk..',
  '..kSssskkssssk..',
  '...kssssssssk...',
  '...kbbbbbbbbk...',
  '..kbbbbbbbbbbk..',
  '..kbkbbkkbbkbk..',
  '..kbkddddbbkbk..',
  '..kskbbbbbbksk..',
  '...kbbbbbbbbk...',
  '...kppppppppk...',
  '...kppk..kppk...',
  '...kppk..kppk...',
  '...kwwk..kwwk...',
];
const DOWN_1 = [
  '................',
  '.....kkkkkk.....',
  '....khhhhhhk....',
  '...khhhhhhhhk...',
  '..khhHHhhhhhhk..',
  '..khHHhhhhhhh k..'.replace(' ', ''),
  '..khsssssssshk..',
  '..kssssssssssk..',
  '..ksekssskessk..',
  '..kssssssssssk..',
  '..kSssskkssssk..',
  '...kssssssssk...',
  '...kbbbbbbbbk...',
  '..kbbbbbbbbbbk..',
  '..kbkbbkkbbkbk..',
  '..kbkddddbbkbk..',
  '..kskbbbbbbksk..',
  '...kbbbbbbbbk...',
  '...kppppppppk...',
  '..kppk....kppk..',
  '.kppk......kppk.',
  '.kwwk......kwwk.',
];
const UP_0 = [
  '................',
  '.....kkkkkk.....',
  '....khhhhhhk....',
  '...khhhhhhhhk...',
  '..khhHHhhhhhhk..',
  '..khHHhhhhhhh k..'.replace(' ', ''),
  '..khhhhhhhhhhk..',
  '..khhhhhhhhhhk..',
  '..khhhhhhhhhhk..',
  '..khhhhhhhhhhk..',
  '..khhhhhhhhhhk..',
  '....khhhhhhk....',
  '...kbbbbbbbbk...',
  '..kbbbbbbbbbbk..',
  '..kbkbbkkbbkbk..',
  '..kbkddddbbkbk..',
  '..kskbbbbbbksk..',
  '...kbbbbbbbbk...',
  '...kppppppppk...',
  '...kppk..kppk...',
  '...kppk..kppk...',
  '...kwwk..kwwk...',
];
const UP_1 = [
  '................',
  '.....kkkkkk.....',
  '....khhhhhhk....',
  '...khhhhhhhhk...',
  '..khhHHhhhhhhk..',
  '..khHHhhhhhhh k..'.replace(' ', ''),
  '..khhhhhhhhhhk..',
  '..khhhhhhhhhhk..',
  '..khhhhhhhhhhk..',
  '..khhhhhhhhhhk..',
  '..khhhhhhhhhhk..',
  '....khhhhhhk....',
  '...kbbbbbbbbk...',
  '..kbbbbbbbbbbk..',
  '..kbkbbkkbbkbk..',
  '..kbkddddbbkbk..',
  '..kskbbbbbbksk..',
  '...kbbbbbbbbk...',
  '...kppppppppk...',
  '..kppk....kppk..',
  '.kppk......kppk.',
  '.kwwk......kwwk.',
];
const SIDE_0 = [
  '................',
  '.....kkkkkk.....',
  '....khhhhhhk....',
  '...khhhhhhhk....',
  '..khhHHhhhhhk...',
  '..khHHhhhhhhk...',
  '..khhhhssshk....',
  '..khhsssssshk...',
  '..khssseksssk...',
  '..kssssssssk....',
  '..kSssskkssk....',
  '...kssssssk.....',
  '...kbbbbbbk.....',
  '..kbbbbbbbk.....',
  '..kbbbbbbbk.....',
  '..kdbbbbbbk.....',
  '..ksbbbbbbk.....',
  '...kbbbbbk......',
  '...kpppppk......',
  '...kpp.kpk......',
  '...kpp.kpk......',
  '...kww.kwk......',
];
const SIDE_1 = [
  '................',
  '.....kkkkkk.....',
  '....khhhhhhk....',
  '...khhhhhhhk....',
  '..khhHHhhhhhk...',
  '..khHHhhhhhhk...',
  '..khhhhssshk....',
  '..khhsssssshk...',
  '..khssseksssk...',
  '..kssssssssk....',
  '..kSssskkssk....',
  '...kssssssk.....',
  '...kbbbbbbk.....',
  '..kbbbbbbbk.....',
  '..kbbbbbbbk.....',
  '..kdbbbbbbk.....',
  '..ksbbbbbbk.....',
  '...kbbbbbk......',
  '...kpppppk......',
  '..kpp...ppk.....',
  '.kpp.....ppk....',
  '.kww.....wwk....',
];
function buildCharSet(palette) {
  const tailored = (rows, direction) => rows.map((row, y) => {
    const pixels = [...row];
    // A two-pixel collar, cuff and pocket distinguish clothing from the skin;
    // the silhouette, stride and 16×22 sprite footprint stay exactly the same.
    if (y === 12 && direction === 'down') { pixels[7] = 'w'; pixels[8] = 'w'; }
    if (y === 15 && direction === 'down') pixels[10] = 'd';
    if (y === 13 && direction === 'up') { pixels[7] = 'd'; pixels[8] = 'd'; }
    if (y === 12 && direction === 'side') pixels[9] = 'w';
    return pixels.join('');
  });
  return {
    down: [makeSprite(tailored(DOWN_0, 'down'), palette), makeSprite(tailored(DOWN_1, 'down'), palette)],
    up: [makeSprite(tailored(UP_0, 'up'), palette), makeSprite(tailored(UP_1, 'up'), palette)],
    right: [makeSprite(tailored(SIDE_0, 'side'), palette), makeSprite(tailored(SIDE_1, 'side'), palette)],
    left: [flipSprite(makeSprite(tailored(SIDE_0, 'side'), palette)), flipSprite(makeSprite(tailored(SIDE_1, 'side'), palette))],
  };
}
const npcSpriteSets = NPC_PALETTES.map(buildCharSet);

/** 把换装结果（已解析颜色）映射为小人调色板 */
export function buildAvatarPalette(resolved) {
  return {
    k: C.ink,
    h: resolved.hair.main, H: resolved.hair.light,
    s: resolved.skin.main, S: resolved.skin.dark,
    e: C.ink,
    b: resolved.shirt.main, d: resolved.shirt.dark,
    p: C.ink, w: C.cream,
  };
}

/** 换装弹窗实时预览：按选中配色即时重绘小人（整数倍放大，保持锐利） */
export function drawAvatarPreview(canvas, resolved, scale = 6) {
  if (!canvas) return;
  const set = buildCharSet(buildAvatarPalette(resolved));
  const img = set.down[0];
  canvas.width = img.width * scale;
  canvas.height = img.height * scale;
  const g = canvas.getContext('2d');
  g.imageSmoothingEnabled = false;
  g.clearRect(0, 0, canvas.width, canvas.height);
  g.drawImage(img, 0, 0, canvas.width, canvas.height);
}

// ============================ 瓦片绘制（32px 精绘） ============================
function drawRect(g, x, y, w, h, color) { g.fillStyle = color; g.fillRect(x, y, w, h); }
function dither(g, x, y, w, h, color) { // 棋盘抖动（像素风质感核心）
  g.fillStyle = color;
  for (let yy = y; yy < y + h; yy += 2) {
    for (let xx = x + (yy % 4 === 0 ? 0 : 1); xx < x + w; xx += 2) {
      g.fillRect(xx, yy, 1, 1);
    }
  }
}
function outline(g, x, y, w, h, color) {
  drawRect(g, x, y, w, 1, color); drawRect(g, x, y + h - 1, w, 1, color);
  drawRect(g, x, y, 1, h, color); drawRect(g, x + w - 1, y, 1, h, color);
}

const TILE_PAINTERS = {
  '.': (g, x, y) => { // 连续错缝木地板：细板缝、稀疏木纹，不重复粗方格
    const tileX = x / TILE, tileY = y / TILE;
    const variation = (tileX * 7 + tileY * 3) % 4;
    drawRect(g, x, y, TILE, TILE, ['#eee8d9', '#ece5d5', '#f0eadc', '#ede7d7'][variation]);
    drawRect(g, x, y + 15, TILE, 1, '#dbd2bf');
    drawRect(g, x, y + 31, TILE, 1, '#dbd2bf');
    drawRect(g, x, y, TILE, 1, '#f7f2e6');
    drawRect(g, x, y + 16, TILE, 1, '#f7f2e6');
    drawRect(g, x + (tileY % 2 ? 7 : 23), y + 1, 1, 14, '#dbd2bf');
    drawRect(g, x + (tileY % 2 ? 23 : 7), y + 17, 1, 14, '#dbd2bf');
    if ((tileX + tileY * 3) % 5 === 0) {
      drawRect(g, x + 12, y + 7, 6, 1, '#e0d7c3');
      drawRect(g, x + 14, y + 8, 3, 1, '#e5dccb');
    }
  },
  '#': (g, x, y) => { // 深青砖墙：砖缝 + 高光顶 + 护墙板
    drawRect(g, x, y, TILE, TILE, C.wall);
    drawRect(g, x, y, TILE, 3, C.wallLight);
    drawRect(g, x, y + 3, TILE, 2, C.wallDark);
    drawRect(g, x, y + 13, TILE, 1, C.wallDark);
    drawRect(g, x + 10, y + 5, 1, 8, C.wallDark);
    drawRect(g, x + 22, y + 14, 1, 8, C.wallDark);
    drawRect(g, x, y + TILE - 5, TILE, 2, C.wallLight);
    drawRect(g, x, y + TILE - 3, TILE, 3, C.wallDark);
  },
  'T': (g, x, y) => { // 工位桌：木纹 + 显示器 + 键盘（屏幕动态层闪烁）
    TILE_PAINTERS['.'](g, x, y);
    drawRect(g, x + 2, y + 11, TILE - 4, 12, C.wood);
    drawRect(g, x + 2, y + 11, TILE - 4, 2, C.woodLight);
    drawRect(g, x + 2, y + 20, TILE - 4, 3, C.woodDark);
    drawRect(g, x + 4, y + 23, 3, 7, C.woodDark);
    drawRect(g, x + TILE - 7, y + 23, 3, 7, C.woodDark);
    // 显示器
    drawRect(g, x + 10, y + 1, 12, 9, C.ink);
    drawRect(g, x + 11, y + 2, 10, 6, C.wallDark); // 屏幕区（动态层覆盖）
    drawRect(g, x + 14, y + 10, 4, 1, C.ink);      // 支架
    // 键盘
    drawRect(g, x + 11, y + 13, 10, 4, C.creamDark);
    drawRect(g, x + 12, y + 14, 8, 1, C.creamShadow);
  },
  'P': (g, x, y) => { // 盆栽：花盆 + 抖动叶丛
    TILE_PAINTERS['.'](g, x, y);
    drawRect(g, x + 10, y + 20, 12, 9, '#c96a28');
    drawRect(g, x + 9, y + 18, 14, 4, '#a85420');
    drawRect(g, x + 11, y + 21, 10, 2, '#e08a45');
    drawRect(g, x + 12, y + 8, 8, 11, C.leaf);
    drawRect(g, x + 8, y + 11, 5, 8, C.leafDark);
    drawRect(g, x + 19, y + 10, 5, 9, C.leafDark);
    drawRect(g, x + 14, y + 5, 4, 4, C.leaf);
    dither(g, x + 10, y + 7, 12, 10, C.leafDark);
  },
  'S': (g, x, y) => { // 沙发：双垫 + 靠包 + 木脚
    TILE_PAINTERS['.'](g, x, y);
    drawRect(g, x + 2, y + 7, TILE - 4, 8, C.mintDark);
    drawRect(g, x + 2, y + 7, TILE - 4, 2, C.mint);
    drawRect(g, x + 2, y + 13, TILE - 4, 9, C.mint);
    drawRect(g, x + 14, y + 13, 2, 9, C.mintDark);
    drawRect(g, x + 4, y + 15, 9, 5, C.mintDark);
    drawRect(g, x + 17, y + 15, 9, 5, C.mintDark);
    drawRect(g, x + 4, y + 22, 3, 6, C.woodDark);
    drawRect(g, x + TILE - 7, y + 22, 3, 6, C.woodDark);
    outline(g, x + 2, y + 7, TILE - 4, 15, C.ink);
  },
  'M': (g, x, y) => { // 咖啡机：机身 + 压力表 + 杯子
    TILE_PAINTERS['.'](g, x, y);
    drawRect(g, x + 5, y + 3, TILE - 10, 26, C.ink);
    drawRect(g, x + 7, y + 5, TILE - 14, 5, C.wallDark);
    drawRect(g, x + 8, y + 6, 3, 3, C.mint);      // 压力表
    drawRect(g, x + 13, y + 6, 5, 2, C.creamDark); // 铭牌
    drawRect(g, x + 10, y + 12, 6, 3, C.wallDark); // 冲煮头
    drawRect(g, x + 11, y + 15, 2, 4, C.woodDark); // 咖啡液
    drawRect(g, x + 9, y + 19, 8, 6, C.cream);     // 杯子
    drawRect(g, x + 17, y + 20, 3, 4, C.cream);
    drawRect(g, x + 7, y + 25, TILE - 14, 2, C.wallDark);
    drawRect(g, x + 20, y + 25, 3, 2, '#d93a3a');  // 指示灯
  },
  'B': (g, x, y) => { // 公告栏：软木 + 便签 + 图钉
    drawRect(g, x, y, TILE, TILE, C.wall);
    drawRect(g, x + 2, y + 4, TILE - 4, 22, C.woodDark);
    drawRect(g, x + 3, y + 5, TILE - 6, 20, C.wood);
    drawRect(g, x + 5, y + 7, 7, 8, C.cream);
    drawRect(g, x + 14, y + 7, 9, 5, C.gold);
    drawRect(g, x + 7, y + 17, 9, 6, C.mint);
    drawRect(g, x + 18, y + 14, 6, 8, C.cream);
    drawRect(g, x + 8, y + 8, 1, 1, '#d93a3a');
    drawRect(g, x + 18, y + 8, 1, 1, '#4a7cc8');
    drawRect(g, x + 11, y + 18, 1, 1, '#4a7cc8');
  },
  'H': (g, x, y) => { // 荣誉墙：奖杯 + 闪光
    drawRect(g, x, y, TILE, TILE, C.wall);
    drawRect(g, x + 2, y + 12, TILE - 4, 3, C.woodDark);
    drawRect(g, x + 2, y + 15, TILE - 4, 2, C.wood);
    // 金奖杯
    drawRect(g, x + 5, y + 4, 6, 5, C.gold);
    drawRect(g, x + 6, y + 9, 4, 2, C.gold);
    drawRect(g, x + 7, y + 11, 2, 2, C.goldDark);
    drawRect(g, x + 4, y + 4, 1, 3, C.goldDark);
    drawRect(g, x + 11, y + 4, 1, 3, C.goldDark);
    // 银相框
    drawRect(g, x + 18, y + 5, 8, 6, C.cream);
    drawRect(g, x + 19, y + 6, 6, 4, C.mint);
    drawRect(g, x + 21, y + 2, 1, 2, C.cream); // 闪光
    drawRect(g, x + 20, y + 3, 3, 1, C.cream);
  },
  'R': (g, x, y) => { // 前台：台面 + 铭牌 + 装饰线
    TILE_PAINTERS['.'](g, x, y);
    drawRect(g, x + 2, y + 5, TILE - 4, 24, C.coral);
    drawRect(g, x + 2, y + 5, TILE - 4, 4, C.coralDark);
    drawRect(g, x + 2, y + 9, TILE - 4, 2, C.woodLight);
    drawRect(g, x + 4, y + 14, TILE - 8, 2, C.coralDark);
    drawRect(g, x + 4, y + 20, TILE - 8, 2, C.coralDark);
    drawRect(g, x + 10, y + 12, 12, 5, C.cream); // 铭牌
    drawRect(g, x + 12, y + 14, 8, 1, C.ink);
    outline(g, x + 2, y + 5, TILE - 4, 24, C.ink);
  },
  'K': (g, x, y) => { // 电钢琴：琴身 + 琴键 + 谱架
    TILE_PAINTERS['.'](g, x, y);
    drawRect(g, x + 2, y + 3, TILE - 4, 6, C.ink);   // 谱架
    drawRect(g, x + 4, y + 4, TILE - 8, 1, C.creamDark);
    drawRect(g, x + 2, y + 10, TILE - 4, 13, C.ink); // 琴身
    for (let i = 0; i < 10; i++) drawRect(g, x + 3 + Math.round(i * 2.6), y + 13, 2, 8, C.cream);
    [3, 6, 10, 13, 17, 20, 24].forEach((kx) => drawRect(g, x + 2 + kx, y + 13, 2, 5, C.ink));
    drawRect(g, x + 5, y + 23, 3, 7, C.ink);
    drawRect(g, x + TILE - 8, y + 23, 3, 7, C.ink);
  },
  'G': (g, x, y) => { // 画廊展墙：动态层会覆盖画作与铭牌
    drawRect(g, x, y, TILE, TILE, '#34393b');
    drawRect(g, x, y, TILE, 3, '#4a5052');
    drawRect(g, x, y + TILE - 4, TILE, 4, '#222628');
    dither(g, x + 2, y + 4, TILE - 4, TILE - 9, '#303537');
    outline(g, x, y, TILE, TILE, C.goldDark);
  },
};

function carpetPainter(base, dark, light) {
  return (g, x, y) => {
    drawRect(g, x, y, TILE, TILE, base);
    drawRect(g, x, y, TILE, 3, dark);
    drawRect(g, x, y + TILE - 3, TILE, 3, dark);
    drawRect(g, x, y, 3, TILE, dark);
    drawRect(g, x + TILE - 3, y, 3, TILE, dark);
    // 内框虚线 + 中心菱花
    drawRect(g, x + 6, y + 6, TILE - 12, 1, light);
    drawRect(g, x + 6, y + TILE - 7, TILE - 12, 1, light);
    drawRect(g, x + 6, y + 6, 1, TILE - 12, light);
    drawRect(g, x + TILE - 7, y + 6, 1, TILE - 12, light);
    drawRect(g, x + 14, y + 12, 4, 4, dark);
    drawRect(g, x + 12, y + 14, 4, 4, dark);
    drawRect(g, x + 16, y + 14, 4, 4, dark);
    drawRect(g, x + 14, y + 16, 4, 4, dark);
  };
}
const MAT_PAINTER = (g, x, y, background) => { // 嵌入式门垫：让通道与操作入口一眼可辨
  if (background) drawRect(g, x, y, TILE, TILE, background);
  else TILE_PAINTERS['.'](g, x, y);
  drawRect(g, x + 3, y + 5, 26, 23, '#b2a38b');
  drawRect(g, x + 3, y + 3, 26, 23, C.ink);
  outline(g, x + 5, y + 5, 22, 19, '#54706e');
  drawRect(g, x + 14, y + 8, 4, 9, C.gold);
  drawRect(g, x + 10, y + 15, 12, 2, C.gold);
  drawRect(g, x + 12, y + 17, 8, 2, C.gold);
  drawRect(g, x + 14, y + 19, 4, 2, C.gold);
};

const RUG_COLORS = {
  a: ['#cfa78d', '#a88066', '#e5c4aa'],
  b: ['#cbbb8d', '#a59362', '#e3d7b0'],
  c: ['#a5beb5', '#7e9c92', '#c5d8ce'],
  d: ['#b6afc2', '#958ba7', '#d3ccdf'],
  e: ['#d5b99c', '#b19373', '#ecd6bd'],
};

function drawZoneRug(g, zone, colors) {
  const x = zone.x * TILE, y = zone.y * TILE;
  const w = zone.w * TILE, h = zone.h * TILE;
  const [base, border, thread] = colors;
  drawRect(g, x + 2, y + 3, w - 4, h - 3, '#bdb29b');
  drawRect(g, x + 1, y + 1, w - 2, h - 3, border);
  drawRect(g, x + 3, y + 3, w - 6, h - 7, base);
  outline(g, x + 6, y + 6, w - 12, h - 13, thread);
  // Stitches follow the perimeter of ONE rug, never the internal tile grid.
  for (let dx = 10; dx < w - 10; dx += 8) {
    drawRect(g, x + dx, y + 3, 2, 1, thread);
    drawRect(g, x + dx, y + h - 5, 2, 1, thread);
  }
  for (let dy = 10; dy < h - 10; dy += 8) {
    drawRect(g, x + 3, y + dy, 1, 2, thread);
    drawRect(g, x + w - 5, y + dy, 1, 2, thread);
  }
}

const CARPETS = {
  a: carpetPainter(C.coral, C.coralDark, '#f6b98a'),
  b: carpetPainter(C.gold, C.goldDark, '#eed78f'),
  c: carpetPainter(C.mint, C.mintDark, '#c2e6e4'),
  d: carpetPainter(C.lavender, C.lavenderDark, '#dccce6'),
  e: carpetPainter(C.peach, C.peachDark, '#ffe3c2'),
};

function prepareSceneLabels(desc, g) {
  const entries = (desc.labels || []).filter(label => !label.zoneLabel).map(label => ({
    text: label.text, size: label.small ? 8 : 10,
    centerX: (label.x + 0.5) * TILE, y: label.y * TILE,
    maxWidth: 180, accent: C.woodDark,
  }));
  for (const zone of desc.zones || []) {
    const isProject = zone.kind === 'production' && zone.projectId != null;
    const isEpisode = zone.kind === 'episode';
    const subtitle = isProject
      ? [`#${zone.projectId}`, zone.subtitle].filter(Boolean).join(' · ')
      : isEpisode ? zone.subtitle : '';
    entries.push({
      text: zone.title || '制作空间', subtitle,
      size: isProject ? 10 : 8,
      centerX: (zone.x + zone.w / 2) * TILE,
      y: zone.y * TILE - (isProject ? 27 : isEpisode ? 20 : 15),
      maxWidth: zone.w * TILE + (isProject ? 0 : 20),
      accent: /延期/.test(subtitle || '') ? C.coralDark : /交付/.test(subtitle || '') ? '#5e8a75' : C.woodDark,
    });
  }
  const fit = (text, available) => {
    const characters = Array.from(String(text || ''));
    if (g.measureText(characters.join('')).width <= available) return characters.join('');
    while (characters.length && g.measureText(`${characters.join('')}…`).width > available) characters.pop();
    return `${characters.join('')}…`;
  };
  return entries.map(entry => {
    g.font = `700 ${entry.size}px ui-monospace, Menlo, monospace`;
    const text = fit(entry.text, entry.maxWidth - 16);
    let width = Math.ceil(g.measureText(text).width) + 16;
    let subtitle = '';
    if (entry.subtitle) {
      g.font = '700 7px ui-monospace, Menlo, monospace';
      subtitle = fit(entry.subtitle, entry.maxWidth - 16);
      width = Math.max(width, Math.ceil(g.measureText(subtitle).width) + 16);
    }
    return {
      ...entry, text, subtitle, width,
      height: entry.size + (subtitle ? 18 : 7),
      x: Math.round(entry.centerX - width / 2), y: Math.round(entry.y),
    };
  });
}

const SOLID = new Set(['#', 'T', 'P', 'S', 'M', 'B', 'H', 'R', 'K', 'G']);

const COFFEE_LINES = [
  '咖啡机：趁热喝，分镜趁热改。',
  '咖啡机：第 37 杯了，注意身体。',
  '咖啡机：好剧本和咖啡一样，要萃。',
  '咖啡机：今天也想拍出爆款吗？',
];

function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

// ============================ 游戏实例 ============================
export function createGame(canvas, hooks = {}) {
  const cb = {
    onPrompt: () => {}, onCrumb: () => {}, onToast: () => {},
    onAction: () => {}, onEscape: () => {},
    ...hooks,
  };
  const ctx = canvas.getContext('2d');

  // 离屏缓冲：场景永远先画到这里（1x）
  const buffer = document.createElement('canvas');
  buffer.width = VIEW_W;
  buffer.height = VIEW_H;
  const bctx = buffer.getContext('2d');
  bctx.imageSmoothingEnabled = false;

  let ZOOM = 2, DPR = 1;
  let zoomCap = 4;
  function fitCanvas() {
    const stage = canvas.parentElement;
    if (!stage) return;
    const availW = stage.clientWidth - 16;
    const availH = stage.clientHeight - 16;
    ZOOM = Math.max(1, Math.min(zoomCap, Math.floor(Math.min(availW / VIEW_W, availH / VIEW_H))));
    DPR = Math.max(1, Math.round(window.devicePixelRatio || 1));
    canvas.width = VIEW_W * ZOOM * DPR;
    canvas.height = VIEW_H * ZOOM * DPR;
    canvas.style.width = `${VIEW_W * ZOOM}px`;
    canvas.style.height = `${VIEW_H * ZOOM}px`;
    ctx.imageSmoothingEnabled = false;
  }

  // ---- 音效（带开关） ----
  let audioCtx = null, soundOn = true;
  function beep(freq, dur = 0.07, type = 'square', gain = 0.03) {
    if (!soundOn || destroyed) return;
    try {
      audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
      const o = audioCtx.createOscillator(), g = audioCtx.createGain();
      o.type = type; o.frequency.value = freq;
      g.gain.setValueAtTime(gain, audioCtx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + dur);
      o.connect(g).connect(audioCtx.destination);
      o.start(); o.stop(audioCtx.currentTime + dur);
    } catch { /* 音频不可用时静默 */ }
  }

  // ---- 场景状态 ----
  let scene = null;          // 当前 sceneDescriptor
  let layer = null;          // 静态层 { canvas, w, h }
  const steam = [];          // 蒸汽粒子
  const artworkImages = new Map(); // 画廊缩略图缓存，仅加载进入视口的作品
  let artworkGeneration = 0;
  let coffeeCooldown = 0;
  let fadeIn = 0;            // 挂载后的淡入（ steps ）

  const player = {
    x: 0, y: 0, dir: 'down', frame: 0, animT: 0, moving: false, speed: 104,
  };
  let playerSprites = buildCharSet(DEFAULT_CHAR_PALETTE);
  let playerName = '新同事';
  let npcs = [];
  let pointerTarget = null;
  let pointerPath = [];
  let pointerStuckElapsed = 0;
  let positionSaveElapsed = 0;
  let currentSpawn = { x: 1.5, y: 1.5, dir: 'down' };

  let paused = false;
  let ambienceEnabled = true;
  let rememberPosition = true;
  let nameTagsVisible = true;
  let destroyed = false;

  function prerender(desc) {
    const rows = desc.grid, w = rows[0].length, h = rows.length;
    const cv = document.createElement('canvas');
    cv.width = w * TILE; cv.height = h * TILE;
    const g = cv.getContext('2d');
    rows.forEach((row, ty) => {
      [...row].forEach((ch, tx) => {
        const x = tx * TILE, y = ty * TILE;
        if (CARPETS[ch]) { CARPETS[ch](g, x, y); return; }
        if ((ch >= '1' && ch <= '9') || ch === 'q') { MAT_PAINTER(g, x, y); return; }
        const painter = TILE_PAINTERS[ch] || TILE_PAINTERS['.'];
        painter(g, x, y);
      });
    });
    const counterCells = new Set();
    for (let ty = 0; ty < h - 1; ty += 1) {
      for (let tx = 0; tx < w - 1; tx += 1) {
        const cells = [[tx, ty], [tx + 1, ty], [tx, ty + 1], [tx + 1, ty + 1]];
        if (cells.some(([cx, cy]) => rows[cy][cx] !== 'R' || counterCells.has(`${cx},${cy}`))) continue;
        cells.forEach(([cx, cy]) => {
          counterCells.add(`${cx},${cy}`);
          TILE_PAINTERS['.'](g, cx * TILE, cy * TILE);
        });
        drawReceptionCounter(g, tx * TILE, ty * TILE, C);
      }
    }
    (desc.zones || []).forEach(zone => {
      // Only decorate a floor/carpet footprint; unknown metadata never
      // paints over walls or changes the collision map.
      const cells = [];
      for (let ty = zone.y; ty < zone.y + zone.h; ty += 1) {
        for (let tx = zone.x; tx < zone.x + zone.w; tx += 1) cells.push({ tx, ty, ch: rows[ty]?.[tx] });
      }
      if (!cells.length || cells.some(({ ch }) => !CARPETS[ch] && !/[.1-9qTK]/.test(ch || '!'))) return;
      const carpet = cells.find(({ ch }) => RUG_COLORS[ch])?.ch || 'a';
      const colors = RUG_COLORS[carpet];
      const propPalette = { ...C, creamShadow: colors[1] };
      drawZoneRug(g, zone, colors);
      cells.forEach(({ tx, ty, ch }) => {
        if (ch === 'T' || ch === 'K') drawStudioProp(g, tx * TILE, ty * TILE, zone.kind, propPalette);
        else if (/[1-9q]/.test(ch)) MAT_PAINTER(g, tx * TILE, ty * TILE, colors[0]);
      });
    });
    return { canvas: cv, w, h, labels: prepareSceneLabels(desc, g) };
  }

  function gridAt(tx, ty) {
    if (!scene) return '#';
    const g = scene.grid;
    if (ty < 0 || ty >= g.length || tx < 0 || tx >= g[0].length) return '#';
    return g[ty][tx];
  }
  function isSolidAt(px, py) {
    return SOLID.has(gridAt(Math.floor(px / TILE), Math.floor(py / TILE)));
  }
  function tryMove(ent, dx, dy) {
    const HW = 7, FOOT = 10;
    if (dx !== 0) {
      const nx = ent.x + dx;
      if (!isSolidAt(nx + Math.sign(dx) * HW, ent.y - FOOT)
        && !isSolidAt(nx + Math.sign(dx) * HW, ent.y - 1)) {
        ent.x = nx;
      }
    }
    if (dy !== 0) {
      const ny = ent.y + dy;
      if (!isSolidAt(ent.x - HW, ny - (dy > 0 ? 1 : FOOT))
        && !isSolidAt(ent.x + HW, ny - (dy > 0 ? 1 : FOOT))) {
        ent.y = ny;
      }
    }
  }

  function rawPositionStorageKey() {
    return scene?.id ? `pixel-town:position:${scene.id}` : null;
  }

  function positionStorageKey() {
    return rememberPosition ? rawPositionStorageKey() : null;
  }

  function savePlayerPosition() {
    const key = positionStorageKey();
    if (!key || !scene) return;
    try {
      localStorage.setItem(key, JSON.stringify({
        x: player.x / TILE,
        y: player.y / TILE,
        dir: player.dir,
      }));
    } catch { /* 无本地存储权限时不影响小镇 */ }
  }

  function loadPlayerPosition(spawn) {
    const key = positionStorageKey();
    if (!key) return null;
    try {
      const saved = JSON.parse(localStorage.getItem(key) || 'null');
      const x = Number(saved?.x);
      const y = Number(saved?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      const px = x * TILE;
      const py = y * TILE;
      if (isSolidAt(px, py) || isSolidAt(px - 7, py - 10) || isSolidAt(px + 7, py - 1)) return null;
      return { x, y, dir: ['up', 'down', 'left', 'right'].includes(saved?.dir) ? saved.dir : spawn.dir };
    } catch {
      return null;
    }
  }

  // ---- 输入 ----
  const keys = new Set();
  let interactQueued = false, escapeQueued = false;
  function isUiControlTarget(target) {
    if (!target) return false;
    const tagName = String(target.tagName || '').toUpperCase();
    if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(tagName) || target.isContentEditable) {
      return true;
    }
    return typeof target.closest === 'function'
      && Boolean(target.closest('[role="button"], [role="dialog"], [data-pixel-town-ignore-keys="true"]'));
  }
  function onKeyDown(e) {
    if (e.defaultPrevented || isUiControlTarget(e.target)) return;
    const k = e.key.toLowerCase();
    if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' '].includes(k)) e.preventDefault();
    if (k === 'enter') { interactQueued = true; return; }
    if (k === 'escape') { escapeQueued = true; return; }
    keys.add(k);
    if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) {
      pointerTarget = null;
      pointerPath = [];
      pointerStuckElapsed = 0;
    }
  }
  function onKeyUp(e) {
    const key = e.key.toLowerCase();
    keys.delete(key);
    if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(key)) {
      savePlayerPosition();
    }
  }
  function onResize() { fitCanvas(); snapCamera(); }
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('resize', onResize);

  function nearestWalkablePoint(worldX, worldY) {
    const baseTx = Math.floor(worldX / TILE);
    const baseTy = Math.floor(worldY / TILE);
    const candidates = [
      [baseTx, baseTy],
      [baseTx, baseTy + 1], [baseTx, baseTy - 1],
      [baseTx + 1, baseTy], [baseTx - 1, baseTy],
      [baseTx + 1, baseTy + 1], [baseTx - 1, baseTy + 1],
      [baseTx + 1, baseTy - 1], [baseTx - 1, baseTy - 1],
    ];
    const walkable = candidates
      .filter(([tx, ty]) => !SOLID.has(gridAt(tx, ty)))
      .map(([tx, ty]) => ({ x: (tx + 0.5) * TILE, y: (ty + 0.72) * TILE }));
    if (walkable.length === 0) return null;
    return walkable.sort((a, b) => (
      Math.hypot(a.x - worldX, a.y - worldY) - Math.hypot(b.x - worldX, b.y - worldY)
    ))[0];
  }

  function findWalkablePath(target) {
    if (!target || !scene) return [];
    const start = [Math.floor(player.x / TILE), Math.floor(player.y / TILE)];
    const goal = [Math.floor(target.x / TILE), Math.floor(target.y / TILE)];
    const keyOf = (tx, ty) => `${tx},${ty}`;
    const startKey = keyOf(...start);
    const goalKey = keyOf(...goal);
    if (startKey === goalKey) return [target];

    const queue = [start];
    const previous = new Map([[startKey, null]]);
    const directions = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (let cursor = 0; cursor < queue.length; cursor += 1) {
      const [tx, ty] = queue[cursor];
      if (keyOf(tx, ty) === goalKey) break;
      directions.forEach(([dx, dy]) => {
        const nx = tx + dx;
        const ny = ty + dy;
        const nextKey = keyOf(nx, ny);
        if (previous.has(nextKey) || SOLID.has(gridAt(nx, ny))) return;
        previous.set(nextKey, [tx, ty]);
        queue.push([nx, ny]);
      });
    }
    if (!previous.has(goalKey)) return [];

    const tiles = [];
    let current = goal;
    while (current && keyOf(...current) !== startKey) {
      tiles.push(current);
      current = previous.get(keyOf(...current));
    }
    tiles.reverse();
    return tiles.map(([tx, ty], index) => (
      index === tiles.length - 1
        ? target
        : { x: (tx + 0.5) * TILE, y: (ty + 0.72) * TILE }
    ));
  }

  function onPointerDown(e) {
    if (paused || !scene || e.button > 0) return;
    canvas.focus({ preventScroll: true });
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const worldX = cam.x + ((e.clientX - rect.left) / rect.width) * VIEW_W;
    const worldY = cam.y + ((e.clientY - rect.top) / rect.height) * VIEW_H;
    const destination = nearestWalkablePoint(worldX, worldY);
    pointerPath = findWalkablePath(destination);
    pointerTarget = pointerPath.shift() || null;
    pointerStuckElapsed = 0;
    keys.clear();
    canvas.setPointerCapture?.(e.pointerId);
  }
  canvas.style.touchAction = 'none';
  canvas.addEventListener('pointerdown', onPointerDown);

  // 侧栏开合等不改变窗口大小但改变舞台尺寸的情况，用 ResizeObserver 兜底
  let resizeObserver = null;
  if (typeof ResizeObserver !== 'undefined' && canvas.parentElement) {
    resizeObserver = new ResizeObserver(() => { fitCanvas(); snapCamera(); });
    resizeObserver.observe(canvas.parentElement);
  }

  // ---- 相机 ----
  const cam = { x: 0, y: 0 };
  function snapCamera() {
    if (!layer) return;
    cam.x = clamp(player.x - VIEW_W / 2, 0, Math.max(0, layer.w * TILE - VIEW_W));
    cam.y = clamp(player.y - VIEW_H / 2, 0, Math.max(0, layer.h * TILE - VIEW_H));
  }

  // ---- NPC 遛弯 AI ----
  function npcThink(npc, dt) {
    if (!layer || npc.stationary) { npc.moving = false; return; }
    if (npc.tx == null) {
      npc.idle -= dt;
      npc.moving = false;
      if (npc.idle <= 0) {
        for (let i = 0; i < 12; i++) {
          const radius = Math.max(0.5, npc.wanderRadius || 3);
          const tx = npc.homeX / TILE + (Math.random() * 2 - 1) * radius;
          const ty = npc.homeY / TILE + (Math.random() * 2 - 1) * radius;
          if (!SOLID.has(gridAt(Math.floor(tx), Math.floor(ty)))) {
            npc.tx = tx * TILE; npc.ty = ty * TILE;
            break;
          }
        }
        npc.idle = 1.5 + Math.random() * 2.5;
      }
      return;
    }
    const dx = npc.tx - npc.x, dy = npc.ty - npc.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 4) { npc.tx = null; npc.moving = false; return; }
    const vx = (dx / dist) * npc.speed * dt, vy = (dy / dist) * npc.speed * dt;
    const beforeX = npc.x, beforeY = npc.y;
    tryMove(npc, vx, vy);
    npc.moving = npc.x !== beforeX || npc.y !== beforeY;
    if (!npc.moving) { npc.tx = null; return; }
    if (Math.abs(dx) > Math.abs(dy)) npc.dir = dx > 0 ? 'right' : 'left';
    else npc.dir = dy > 0 ? 'down' : 'up';
    npc.animT += dt;
    if (npc.animT > 0.16) { npc.animT = 0; npc.frame = 1 - npc.frame; }
  }

  // ---- 交互 ----
  function activeInteraction() {
    if (!scene) return null;
    const tx = Math.floor(player.x / TILE), ty = Math.floor(player.y / TILE);
    const mat = (scene.mats || []).find((m) => m.tx === tx && m.ty === ty);
    if (mat) return { kind: 'mat', label: mat.label, target: mat };

    const hotspot = (scene.hotspots || []).find((item) => {
      const cx = (item.tx + 0.5) * TILE;
      const cy = (item.ty + 0.5) * TILE;
      return Math.hypot(player.x - cx, player.y - cy) <= (item.range || 1.45) * TILE;
    });
    if (hotspot) return { kind: 'hotspot', label: hotspot.label, target: hotspot };

    const npc = npcs.find((item) => (
      Math.hypot(player.x - item.x, player.y - item.y) <= 1.15 * TILE
    ));
    if (npc) {
      const suffix = npc.activity ? ` · ${npc.activity}` : '';
      return { kind: 'npc', label: `和 ${npc.name} 聊聊${suffix}`, target: npc };
    }
    return null;
  }

  function triggerInteraction(interaction) {
    if (!interaction) return;
    beep(392, 0.09);
    if (interaction.kind === 'npc') {
      const npc = interaction.target;
      const lines = Array.isArray(npc.lines) && npc.lines.length > 0
        ? npc.lines
        : ['今天也在稳稳推进。'];
      const line = lines[npc.lineIndex % lines.length];
      npc.lineIndex += 1;
      cb.onToast(`${npc.name}：${line}`);
      return;
    }
    setTimeout(() => beep(587, 0.09), 90);
    cb.onAction(interaction.target.action, interaction.target);
  }

  function checkCoffee(dt) {
    if (!scene || !scene.coffee) return;
    coffeeCooldown -= dt;
    const cx = (scene.coffee.tx + 0.5) * TILE, cy = (scene.coffee.ty + 0.5) * TILE;
    const dx = player.x - cx, dy = player.y - cy;
    if (Math.hypot(dx, dy) < TILE * 1.6 && coffeeCooldown <= 0) {
      coffeeCooldown = 8;
      cb.onToast(COFFEE_LINES[Math.floor(Math.random() * COFFEE_LINES.length)]);
      beep(880, 0.05, 'square', 0.02);
    }
  }

  function updateSteam(dt) {
    if (!scene || !scene.coffee) return;
    const cx = (scene.coffee.tx + 0.5) * TILE, cy = (scene.coffee.ty + 0.5) * TILE;
    if (Math.random() < dt * 3) {
      steam.push({ x: cx + 13 + Math.random() * 5, y: cy + 5, life: 1 });
    }
    for (let i = steam.length - 1; i >= 0; i--) {
      const p = steam[i];
      p.y -= dt * 13; p.life -= dt * 0.9;
      if (p.life <= 0) steam.splice(i, 1);
    }
  }

  // ---- 绘制 ----
  // 文字不进缓冲：每帧记录待绘文字，合成放大后在设备分辨率下原生绘制（否则小字被二次采样发虚）
  const frameTexts = [];
  const frameMedia = [];
  let camRX = 0, camRY = 0;

  function drawChar(g, sprites, ent, isPlayer) {
    const set = sprites[ent.dir] || sprites.down;
    const img = set[ent.moving ? ent.frame : 0];
    const dx = Math.round(ent.x - 8), dy = Math.round(ent.y - 21);
    g.globalAlpha = 0.18;
    drawRect(g, dx + 3, dy + 21, 10, 3, C.ink);
    drawRect(g, dx + 1, dy + 22, 14, 1, C.ink);
    g.globalAlpha = 1;
    if (isPlayer) {
      drawRect(g, dx + 1, dy + 24, 4, 1, C.coralDark);
      drawRect(g, dx + 11, dy + 24, 4, 1, C.coralDark);
    }
    g.drawImage(img, dx, dy);
    g.globalAlpha = 1;
  }

  function drawNameTag(g, ent) {
    g.font = '700 7px ui-monospace, Menlo, monospace';
    const w = Math.ceil(g.measureText(ent.name).width) + 8;
    const x = Math.round(ent.x - w / 2), y = Math.round(ent.y - 33);
    g.globalAlpha = 0.9;
    drawRect(g, x, y, w, 11, C.ink);
    g.globalAlpha = 1;
    frameTexts.push({ text: ent.name, x: x + 4 - camRX, y: y + 2 - camRY, size: 7, color: C.cream });
  }

  function ensureArtworkImage(artwork) {
    const url = String(artwork?.thumbnailUrl || '').trim();
    if (!url) return null;
    const key = `${artwork.id}:${url}`;
    const cached = artworkImages.get(key);
    if (cached) return cached;

    const generation = artworkGeneration;
    const image = new Image();
    const record = { image, status: 'loading', generation };
    artworkImages.set(key, record);
    image.decoding = 'async';
    image.onload = () => {
      if (!destroyed && generation === artworkGeneration) record.status = 'ready';
    };
    image.onerror = () => {
      if (generation === artworkGeneration) record.status = 'error';
    };
    image.src = url;
    return record;
  }

  function drawContainedImage(g, image, x, y, width, height) {
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    if (!sourceWidth || !sourceHeight) return;
    const scale = Math.min(width / sourceWidth, height / sourceHeight);
    const drawWidth = Math.max(1, Math.round(sourceWidth * scale));
    const drawHeight = Math.max(1, Math.round(sourceHeight * scale));
    const drawX = Math.round(x + (width - drawWidth) / 2);
    const drawY = Math.round(y + (height - drawHeight) / 2);
    g.imageSmoothingEnabled = true;
    g.drawImage(image, drawX, drawY, drawWidth, drawHeight);
    g.imageSmoothingEnabled = false;
  }

  function drawArtworks(g) {
    (scene?.artworks || []).forEach((artwork) => {
      const x = artwork.tx * TILE;
      const y = artwork.ty * TILE;
      const width = Math.max(TILE, (artwork.widthTiles || 1) * TILE);
      const height = Math.max(TILE, (artwork.heightTiles || 1) * TILE);
      if (x + width < camRX - TILE || x > camRX + VIEW_W + TILE
        || y + height < camRY - TILE || y > camRY + VIEW_H + TILE) return;

      const frameX = x + 3;
      const frameY = y + 3;
      const frameWidth = width - 6;
      const frameHeight = height - 6;
      const labelHeight = 13;
      const imageX = frameX + 4;
      const imageY = frameY + 4;
      const imageWidth = frameWidth - 8;
      const imageHeight = frameHeight - labelHeight - 8;

      drawRect(g, frameX, frameY, frameWidth, frameHeight, C.woodDark);
      outline(g, frameX, frameY, frameWidth, frameHeight, C.gold);
      drawRect(g, imageX, imageY, imageWidth, imageHeight, '#171b1d');

      const record = ensureArtworkImage(artwork);
      if (record?.status === 'ready') {
        drawContainedImage(g, record.image, imageX, imageY, imageWidth, imageHeight);
        frameMedia.push({
          image: record.image,
          x: imageX - camRX,
          y: imageY - camRY,
          width: imageWidth,
          height: imageHeight,
          isVideo: Boolean(artwork.isVideo),
        });
      } else {
        drawRect(g, imageX + Math.floor(imageWidth / 2) - 7, imageY + Math.floor(imageHeight / 2) - 5, 14, 10, '#3f474a');
        drawRect(g, imageX + Math.floor(imageWidth / 2) - 4, imageY + Math.floor(imageHeight / 2) - 2, 3, 3, C.gold);
        drawRect(g, imageX + Math.floor(imageWidth / 2) + 1, imageY + Math.floor(imageHeight / 2), 4, 3, C.mintDark);
      }

      if (artwork.isVideo) {
        const playX = imageX + Math.floor(imageWidth / 2) - 4;
        const playY = imageY + Math.floor(imageHeight / 2) - 5;
        g.fillStyle = 'rgba(22, 28, 30, 0.82)';
        g.fillRect(playX - 4, playY - 3, 15, 16);
        g.fillStyle = C.cream;
        g.beginPath();
        g.moveTo(playX, playY);
        g.lineTo(playX, playY + 10);
        g.lineTo(playX + 8, playY + 5);
        g.closePath();
        g.fill();
      }

      const labelY = frameY + frameHeight - labelHeight;
      drawRect(g, frameX + 1, labelY, frameWidth - 2, labelHeight - 1, 'rgba(24, 29, 31, 0.94)');
      const title = String(artwork.name || '未命名作品');
      const shortTitle = title.length > 9 ? `${title.slice(0, 8)}…` : title;
      frameTexts.push({
        text: shortTitle,
        x: frameX + 5 - camRX,
        y: labelY + 3 - camRY,
        size: 7,
        color: C.cream,
      });
      if (artwork.duration) {
        frameTexts.push({
          text: artwork.duration,
          x: frameX + frameWidth - Math.max(24, String(artwork.duration).length * 5) - camRX,
          y: labelY + 3 - camRY,
          size: 7,
          color: C.gold,
        });
      }
    });
  }

  function drawFrameMedia() {
    const outputScale = ZOOM * DPR;
    frameMedia.forEach((media) => {
      const x = Math.round(media.x * outputScale);
      const y = Math.round(media.y * outputScale);
      const width = Math.max(1, Math.round(media.width * outputScale));
      const height = Math.max(1, Math.round(media.height * outputScale));

      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, width, height);
      ctx.clip();
      drawContainedImage(ctx, media.image, x, y, width, height);
      ctx.restore();

      if (!media.isVideo) return;
      const unit = Math.max(1, outputScale);
      const centerX = x + width / 2;
      const centerY = y + height / 2;
      ctx.save();
      ctx.fillStyle = 'rgba(17, 20, 21, 0.8)';
      ctx.beginPath();
      ctx.arc(centerX, centerY, 8 * unit, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = C.cream;
      ctx.beginPath();
      ctx.moveTo(centerX - 2 * unit, centerY - 5 * unit);
      ctx.lineTo(centerX - 2 * unit, centerY + 5 * unit);
      ctx.lineTo(centerX + 6 * unit, centerY);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    });
  }

  function drawLabels(g) {
    (layer?.labels || []).forEach((label) => {
      const { x, y, width, height, size } = label;
      if (x + width < camRX || x > camRX + VIEW_W || y + height < camRY || y > camRY + VIEW_H) return;
      drawRect(g, x + 1, y, width - 2, height, '#899b96');
      drawRect(g, x, y + 1, width, height - 2, '#899b96');
      drawRect(g, x + 1, y + 1, width - 2, height - 2, C.cream);
      drawRect(g, x + 3, y + 4, 2, height - 8, label.accent);
      frameTexts.push({ text: label.text, x: x + 9 - camRX, y: y + 4 - camRY, size, color: C.ink });
      if (label.subtitle) {
        frameTexts.push({ text: label.subtitle, x: x + 9 - camRX, y: y + size + 8 - camRY, size: 7, color: '#6b7369' });
      }
    });
  }

  let lastT = 0;
  let lastPrompt = null;
  let rafId = 0;
  function frame(t) {
    if (destroyed) return;
    const dt = Math.min(0.05, (t - lastT) / 1000 || 0);
    lastT = t;

    // ---- 玩家移动 ----
    let mx = 0, my = 0;
    let pointerDistance = null;
    if (!paused && scene) {
      if (keys.has('w') || keys.has('arrowup')) my -= 1;
      if (keys.has('s') || keys.has('arrowdown')) my += 1;
      if (keys.has('a') || keys.has('arrowleft')) mx -= 1;
      if (keys.has('d') || keys.has('arrowright')) mx += 1;
      if (mx === 0 && my === 0 && pointerTarget) {
        const dx = pointerTarget.x - player.x;
        const dy = pointerTarget.y - player.y;
        const dist = Math.hypot(dx, dy);
        if (dist <= 3) {
          pointerTarget = pointerPath.shift() || null;
          pointerStuckElapsed = 0;
          if (!pointerTarget) savePlayerPosition();
        } else {
          mx = dx / dist;
          my = dy / dist;
          pointerDistance = dist;
        }
      }
    }
    player.moving = mx !== 0 || my !== 0;
    if (player.moving) {
      const len = Math.hypot(mx, my);
      const step = pointerDistance == null
        ? player.speed * dt
        : Math.min(player.speed * dt, pointerDistance);
      const beforeX = player.x;
      const beforeY = player.y;
      tryMove(player, (mx / len) * step, (my / len) * step);
      const didMove = Math.hypot(player.x - beforeX, player.y - beforeY) > 0.05;
      player.moving = didMove;
      if (pointerTarget) {
        pointerStuckElapsed = didMove ? 0 : pointerStuckElapsed + dt;
        if (pointerStuckElapsed >= 0.35) {
          pointerTarget = null;
          pointerPath = [];
          pointerStuckElapsed = 0;
          player.moving = false;
        }
      }
      if (Math.abs(mx) > Math.abs(my)) player.dir = mx > 0 ? 'right' : 'left';
      else if (my !== 0) player.dir = my > 0 ? 'down' : 'up';
      player.animT += dt;
      if (player.animT > 0.13) { player.animT = 0; player.frame = 1 - player.frame; }
    } else {
      player.frame = 0;
    }
    positionSaveElapsed += dt;
    if (player.moving && positionSaveElapsed >= 0.75) {
      positionSaveElapsed = 0;
      savePlayerPosition();
    }

    // ---- NPC / 环境 ----
    npcs.forEach((n) => npcThink(n, dt));
    checkCoffee(dt);
    updateSteam(dt);

    // ---- 交互 ----
    const interaction = paused ? null : activeInteraction();
    const promptLabel = interaction ? interaction.label : null;
    if (promptLabel !== lastPrompt) {
      lastPrompt = promptLabel;
      cb.onPrompt(promptLabel);
    }
    if (interactQueued) {
      interactQueued = false;
      triggerInteraction(interaction);
    }
    if (escapeQueued) {
      escapeQueued = false;
      if (!paused) cb.onEscape();
    }

    // ---- 相机 ----
    if (layer) {
      const targetX = clamp(player.x - VIEW_W / 2, 0, Math.max(0, layer.w * TILE - VIEW_W));
      const targetY = clamp(player.y - VIEW_H / 2, 0, Math.max(0, layer.h * TILE - VIEW_H));
      cam.x += (targetX - cam.x) * Math.min(1, dt * 6);
      cam.y += (targetY - cam.y) * Math.min(1, dt * 6);
    }

    // ---- 场景绘制到离屏缓冲（1x） ----
    bctx.setTransform(1, 0, 0, 1, 0, 0);
    bctx.clearRect(0, 0, VIEW_W, VIEW_H);
    if (layer) {
      bctx.save();
      frameTexts.length = 0;
      frameMedia.length = 0;
      camRX = Math.round(cam.x);
      camRY = Math.round(cam.y);
      bctx.translate(-camRX, -camRY);

      bctx.drawImage(layer.canvas, 0, 0);

      drawArtworks(bctx);

      if (ambienceEnabled && scene?.ambience === 'local') {
        const hour = new Date().getHours();
        const night = hour < 6 || hour >= 20;
        const evening = !night && (hour < 8 || hour >= 17);
        if (night || evening) {
          bctx.globalAlpha = night ? 0.13 : 0.06;
          drawRect(bctx, camRX, camRY, VIEW_W, VIEW_H, night ? '#102c46' : '#d8893b');
          bctx.globalAlpha = 1;
        }
      }

      // 蒸汽
      steam.forEach((p) => {
        bctx.globalAlpha = Math.max(0, p.life) * 0.7;
        drawRect(bctx, p.x, p.y, 2, 2, C.cream);
        bctx.globalAlpha = 1;
      });

      drawLabels(bctx);

      const drawables = [
        ...npcs.map((n) => ({ y: n.y, draw: () => drawChar(bctx, n.sprites, n, false) })),
        { y: player.y, draw: () => drawChar(bctx, playerSprites, player, true) },
        ...(nameTagsVisible
          ? [
            { y: player.y + 0.1, draw: () => drawNameTag(bctx, { x: player.x, y: player.y, name: playerName }) },
            ...npcs.map((n) => ({ y: n.y + 0.1, draw: () => drawNameTag(bctx, n) })),
          ]
          : []),
      ].sort((a, b) => a.y - b.y);
      drawables.forEach((d) => d.draw());

      bctx.restore();

      // 挂载淡入（steps 像素感）
      if (fadeIn > 0) {
        fadeIn = Math.max(0, fadeIn - dt);
        const step = Math.ceil((fadeIn / 0.35) * 4) / 4;
        bctx.globalAlpha = step;
        drawRect(bctx, 0, 0, VIEW_W, VIEW_H, C.wallDark);
        bctx.globalAlpha = 1;
      }
    }

    // ---- 整数倍放大合成到主画布（锐利的根源） ----
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(buffer, 0, 0, VIEW_W * ZOOM * DPR, VIEW_H * ZOOM * DPR);

    // 画作不经过 480x270 像素缓冲，直接按设备分辨率覆盖绘制，避免缩略图被放大成马赛克。
    if (fadeIn <= 0) drawFrameMedia();

    // ---- 文字在设备分辨率下原生绘制（不参与像素放大，保持锐利） ----
    ctx.textBaseline = 'top';
    frameTexts.forEach((t) => {
      ctx.font = `700 ${t.size * ZOOM * DPR}px ui-monospace, Menlo, monospace`;
      ctx.fillStyle = t.color;
      ctx.fillText(t.text, t.x * ZOOM * DPR, t.y * ZOOM * DPR);
    });

    rafId = requestAnimationFrame(frame);
  }

  // ============================ 对外接口 ============================
  function mount(sceneDescriptor) {
    savePlayerPosition();
    scene = sceneDescriptor;
    artworkGeneration += 1;
    artworkImages.clear();
    layer = prerender(sceneDescriptor);
    steam.length = 0;
    coffeeCooldown = 0;
    const spawn = sceneDescriptor.spawn || { x: 1.5, y: 1.5 };
    currentSpawn = { ...spawn, dir: spawn.dir || 'down' };
    const savedPosition = loadPlayerPosition(currentSpawn);
    const start = savedPosition || currentSpawn;
    player.x = start.x * TILE; player.y = start.y * TILE;
    player.dir = start.dir || 'down'; player.frame = 0; player.animT = 0; player.moving = false;
    pointerTarget = null;
    pointerPath = [];
    pointerStuckElapsed = 0;
    positionSaveElapsed = 0;
    npcs = (sceneDescriptor.npcs || []).map((n, i) => ({
      name: n.name,
      x: n.x * TILE, y: n.y * TILE,
      homeX: n.x * TILE, homeY: n.y * TILE,
      dir: 'down', frame: 0, animT: 0, moving: false,
      speed: n.speed || 50 + (i % 2) * 6,
      sprites: npcSpriteSets[(n.paletteIndex ?? i) % npcSpriteSets.length],
      tx: null, ty: null, idle: 1 + i,
      activity: n.activity || '',
      lines: n.lines || [],
      lineIndex: 0,
      wanderRadius: n.wanderRadius,
      stationary: !!n.stationary,
    }));
    lastPrompt = null;
    cb.onPrompt(null);
    cb.onCrumb(sceneDescriptor.crumb || '');
    fadeIn = 0.35;
    snapCamera();
  }

  function setAvatar(resolved) {
    if (!resolved) return;
    playerSprites = buildCharSet(buildAvatarPalette(resolved));
    playerName = resolved.name || '新同事';
  }

  function walkToTile(tx, ty) {
    const tileX = Number(tx);
    const tileY = Number(ty);
    if (!scene || !Number.isFinite(tileX) || !Number.isFinite(tileY)) return false;
    const destination = nearestWalkablePoint((tileX + 0.5) * TILE, (tileY + 0.72) * TILE);
    const nextPath = findWalkablePath(destination);
    if (nextPath.length === 0) return false;
    pointerPath = nextPath;
    pointerTarget = pointerPath.shift() || null;
    pointerStuckElapsed = 0;
    keys.clear();
    return true;
  }

  function resetPosition() {
    if (!scene) return false;
    const key = rawPositionStorageKey();
    if (key) {
      try { localStorage.removeItem(key); } catch { /* 无本地存储权限时仍可回入口 */ }
    }
    player.x = currentSpawn.x * TILE;
    player.y = currentSpawn.y * TILE;
    player.dir = currentSpawn.dir || 'down';
    player.frame = 0;
    player.animT = 0;
    player.moving = false;
    pointerTarget = null;
    pointerPath = [];
    pointerStuckElapsed = 0;
    keys.clear();
    savePlayerPosition();
    snapCamera();
    return true;
  }

  function destroy() {
    savePlayerPosition();
    destroyed = true;
    artworkGeneration += 1;
    artworkImages.clear();
    cancelAnimationFrame(rafId);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('resize', onResize);
    canvas.removeEventListener('pointerdown', onPointerDown);
    if (resizeObserver) { resizeObserver.disconnect(); resizeObserver = null; }
    keys.clear();
    if (audioCtx) { try { audioCtx.close(); } catch { /* 忽略 */ } audioCtx = null; }
  }

  fitCanvas();
  rafId = requestAnimationFrame(frame);

  return {
    mount,
    destroy,
    setAvatar,
    walkToTile,
    resetPosition,
    setSound(on) { soundOn = !!on; },
    isSoundOn() { return soundOn; },
    setTownSize(size) {
      zoomCap = size === 'small' ? 2 : size === 'medium' ? 3 : 4;
      fitCanvas();
      snapCamera();
    },
    setMovementSpeed(speed) {
      player.speed = speed === 'relaxed' ? 82 : speed === 'fast' ? 132 : 104;
    },
    setAmbience(on) { ambienceEnabled = !!on; },
    setRememberPosition(on) {
      const key = rawPositionStorageKey();
      rememberPosition = !!on;
      if (!rememberPosition && key) {
        try { localStorage.removeItem(key); } catch { /* 无本地存储权限时忽略 */ }
      } else if (rememberPosition) {
        savePlayerPosition();
      }
    },
    setNameTags(on) { nameTagsVisible = !!on; },
    setPaused(p) {
      paused = !!p;
      if (paused) {
        savePlayerPosition();
        keys.clear();
        pointerTarget = null;
        pointerPath = [];
        pointerStuckElapsed = 0;
        interactQueued = false;
        escapeQueued = false;
      }
    },
    interact() { interactQueued = true; },
  };
}
